import React from 'react';

import { APIResponse, APITypeaheadItem } from '@cd3p/core/types/api';
import { textField } from '@cd3p/core/utils/fields';
import { FormField, TypeaheadFormField } from 'components';

import { Controller, FieldError, useFormContext } from 'third-party';

import { useCache } from 'modules/cache';

import { TypeaheadOption } from 'types/app';

type CustomerMultiselectFieldProps = {
  fieldName: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  requiredErrorMessage?: string;
  fieldError?: FieldError | undefined;
};

export const CustomerMultiselectField: React.FC<
  CustomerMultiselectFieldProps
> = ({
  fieldName,
  label,
  placeholder,
  disabled,
  requiredErrorMessage,
  fieldError,
}) => {
  const { loadCustomers } = useCache();
  const { control, setValue } = useFormContext();

  const loadCustomersOptions = async (inputValue: string) => {
    try {
      const result: APIResponse<APITypeaheadItem[]> = await loadCustomers(
        inputValue,
      );

      if (!result.error && result.payload) {
        return {
          options: result.payload.map(item => ({
            label: item.name,
            value: item.id,
            data: item,
          })),
        };
      }
      return { options: [] };
    } catch {
      return { options: [] };
    }
  };

  const onCustomerChange = (selectedOption: TypeaheadOption[] | null) => {
    setValue(fieldName, selectedOption || [], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field: { value } }) => (
        <FormField
          fieldName={fieldName}
          control={control}
          label={label}
          fieldError={fieldError}
          requiredErrorMessage={requiredErrorMessage}
          render={() => (
            <TypeaheadFormField
              openMenuOnFocus
              openMenuOnClick
              defaultOptions
              isCreatable={false}
              isMulti
              placeholder={placeholder}
              loadOptions={loadCustomersOptions}
              onChange={onCustomerChange}
              value={value}
              maxLength={textField.maxLength}
              isDisabled={disabled}
              hasError={!!fieldError}
              menuPortalTarget={document.body}
            />
          )}
        />
      )}
    />
  );
};
